<template>
  <v-container class="my-campaigns-page pa-0 pb-8">
    <v-row>
      <v-col
        cols="12"
        class="my-campaigns-page__title"
        :class="{ 'mt-3': !isPageInIframe }"
      >
        <span>My campaigns</span>

        <cg-button
          v-if="showCreateCampaignButton && userCanCreateCampaign"
          :height="46"
          :to="{ name: 'CreateCampaignName', params: { campaignId: 'create' } }"
        >
          <template #prepend-icon>
            <icons-add />
          </template>

          Create new campaign
        </cg-button>
      </v-col>

      <v-col cols="12" class="my-campaigns-page__failed-physical-automation-sends">
        <CgAlert type="error" v-if="failedAutomationActionsCount">
          <div class="my-campaigns-page__failed-physical-automation-sends--text">
            {{ failedAutomationActionsCount }} automated {{ failedAutomationCampaignLabel }} sends failed to deliver
          </div>
          <div
            class="my-campaigns-page__failed-physical-automation-sends--review-btn"
            @click="showFailedAutomatedPhysicalSends"
          >
            Review and resolve
          </div>
        </CgAlert>
      </v-col>

      <v-col cols="12">
        <v-row justify="end">
          <v-col cols="12">
            <v-row no-gutters class="flex-nowrap g10" align="center">
              <v-col class="flex-shrink-1 flex-grow-0">
                <the-campaign-search-input
                  :initial-search.sync="search"
                  :campaign-id.sync="campaignId"
                  :creator-id.sync="creatorId"
                />
              </v-col>
              <v-col class="flex-shrink-1 flex-grow-0">
                <common-select
                  v-model="selectedTag"
                  :items="availableTags"
                  placeholder="Tags"
                  item-value="id"
                  item-text="name"
                  style="width: 200px; max-width: 200px"
                  :height="30"
                  filter-style
                />
              </v-col>
              <v-col class="flex-shrink-1 flex-grow-0">
                <common-select
                  v-model="sortBy"
                  :items="sortByItems"
                  placeholder="Sort by"
                  style="width: 160px; max-width: 160px"
                  :height="30"
                  filter-style
                />
              </v-col>
              <v-col class="flex-shrink-1 flex-grow-0">
                <common-select
                  v-model="filterBy"
                  :items="filterByItems"
                  placeholder="Filter by:"
                  style="width: 220px; max-width: 220px"
                  :height="30"
                  filter-style
                />
              </v-col>

              <v-col class="flex-shrink-0 flex-grow-1">
                <common-checkbox v-model="showDisabledCampaigns">
                  Show disabled campaigns
                </common-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-container class="pa-0 my-4">
      <common-loader v-if="loading" style="padding: 250px 0" />

      <v-row v-if="!loading && campaignItems.length">
        <template v-for="campaign in campaignItems">
          <the-campaign-card
            :key="campaign.entity_id"
            :item="campaign"
            :user-permissions="userPermissions"
            @showGiftOptionsPanel="showGiftOptionsPanel"
            @showEstCostPanel="showEstCostPanel"
            @showAutomationPanel="showAutomationPanel"
            @showEGiftPanel="showEGiftPanel"
            @showCampaignSettingsPanel="showCampaignSettingsPanel"
            @showSendDirectPanel="showSendDirectPanel(campaign)"
          />
        </template>
      </v-row>

      <div
        v-else-if="!loading && !campaignItems.length"
        class="my-campaigns-page__empty"
      >
        <icons-empty-campaigns :width="118" :height="91" />

        <span>
          {{
            search
              ? 'No campaigns/creators found'
              : 'Campaigns are the easiest and fastest way to send gifts for recurring occasions (e.g. employees onboarding, birthdays, clients anniversaries, etc.)'
          }}


        </span>

        <cg-button
          v-if="userCanCreateCampaign"
          :height="46"
          :to="{ name: 'CreateCampaignName', params: { campaignId: 'create' } }"
        >
          <template #prepend-icon>
            <icons-add />
          </template>

          Create new campaign
        </cg-button>
      </div>

      <div v-if="!loading && canLoadMorePages" class="my-campaigns-page__load-more">
        <load-more
          :is-visible="canLoadMorePages"
          :loading="loadingPage"
          :size="loadMoreNumber"
          @more="handleLoadMore"
        />
      </div>
    </v-container>

    <gift-options-panel
      v-model="giftOptionsPanel"
      :gift="selectedGift"
    />

    <the-est-cost-panel
      v-model="estCostPanel"
      :gift="selectedGift"
    />

    <the-campaign-settings-panel
      v-model="campaignSettingsPanel"
      :gift="selectedGift"
      :availableTags="availableTags"
      @fetchData="fetchData()"
    />
    <!-- view-mode -->
    <send-egift-panel
      v-model="sendEGiftPanel"
      :campaign="selectedGift"
      :panelType="sendEGiftPanelType"
      :marketplaceUser="!userIsCgElite"
    />
    <!-- default mode -->
    <old-send-egift-panel
      v-model="sendEGiftPanelOld"
      :gift="selectedGift"
      :panelType="sendEGiftPanelType"
      :marketplaceUser="!userIsCgElite"
      @fetchData="fetchData()"
    />

    <automation-panel
      v-model="automationPanel"
      :campaign="selectedGift"
      @automationCreated="fetchData()"
      @automationRemoved="fetchData()"
    />

    <the-failed-sends-panel
      v-model="failedAutomatedPhysicalSendsPanel"
      @fetchData="fetchData()"
    />
  </v-container>
</template>

<script>
import axios from 'axios'
import Api from '@/axios/api'
import setCookie from '@/utils/setCookie'
import { createNamespacedHelpers } from 'vuex'

import { CgButton, CgAlert } from '@corporategiftcom/design-system'
import LoadMore from '@/components/LoadMore'
import TheCampaignCard from '../components/myCampaign/TheCampaignCard.vue'
import GiftOptionsPanel from '../components/myCampaign/panels/TheGiftOptionsPanel.vue'
import TheEstCostPanel from '../components/myCampaign/panels/TheEstCostPanel.vue'
import TheCampaignSettingsPanel from '../components/myCampaign/panels/TheCampaignSettingsPanel.vue'
import SendEgiftPanel from '@/components/myCampaign/panels/SendEgiftPanel.vue'
import OldSendEgiftPanel from '../components/myCampaign/panels/TheSendEgiftPanel.vue'
import AutomationPanel from '@/components/myCampaign/panels/TheAutomationCampaignPanel.vue'
import TheCampaignSearchInput from '@/components/myCampaign/TheCampaignSearchInput.vue'

import isPageInIframe from '@/components/myCampaign/panels/mixins/isPageInIframe'
import superAdmin from '@/mixins/superAdmin'
import adminAccount from '@/mixins/adminAccount'
import masterAccount from '@/mixins/masterAccount'
import userIsCgElite from '@/mixins/userIsCgElite'
import campaignsFilters from '@/components/myCampaign/mixins/campaignsFilters'
import updateCartAmount from "@/mixins/updateCartAmount";
import TheFailedSendsPanel from "@/components/myCampaign/panels/failedSends/TheFailedSendsPanel.vue";

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('myCampaigns')

export default {
  name: 'MyCampaignsPage',
  components: {
    LoadMore,
    TheFailedSendsPanel,
    TheCampaignSettingsPanel,
    TheCampaignSearchInput,
    OldSendEgiftPanel,
    AutomationPanel,
    SendEgiftPanel,
    GiftOptionsPanel,
    TheCampaignCard,
    TheEstCostPanel,
    CgButton,
    CgAlert
  },
  mixins: [
    campaignsFilters,
    isPageInIframe,
    userIsCgElite,
    masterAccount,
    adminAccount,
    superAdmin,
    updateCartAmount,
  ],
  data: () => ({
    loading: false,
    selectedGift: null,
    giftOptionsPanel: false,
    estCostPanel: false,
    campaignSettingsPanel: false,
    sendEGiftPanel: false,
    sendEGiftPanelOld: false,
    automationPanel: false,
    failedAutomatedPhysicalSendsPanel: false,
    sendEGiftPanelType: 'egift',
    userPermissions: [],
    failedAutomationActionsCount: null,
    getCampaignsController: null,
    getTagsController: null,
    loadMoreController: null,
  }),
  computed: {
    ...mapState(['campaignItems', 'loadingPage', 'meta']),
    ...mapGetters(['canLoadMorePages']),
    userCanCreateCampaign () {
      return this.userPermissions.includes('create')
    },
    showCreateCampaignButton () {
      const { loading, campaignItems } = this

      if (loading) { return false }
      return !!campaignItems.length
    },
    routeParams () {
      return this.$route.query ?? {}
    },
    loadMoreNumber () {
      const { currentPage, lastPage, perPage, total, to } = this.meta

      if (!lastPage || !perPage) { return 0 }
      const nextPage = currentPage + 1
      return nextPage === lastPage ? total - to : perPage
    },
    failedAutomationCampaignLabel () {
      return this.failedAutomationActionsCount === 1 ? 'campaign' : 'campaigns'
    },
  },
  watch: {
    queryParams: {
      handler () {
        if (!this._initialMount) this.fetchData()
      },
      immediate: false
    },
    automationPanel (val) {
      if (!val) { this.clearRouteParams() }
    },
    selectedTag (val) {
      if (!val) { this.clearRouteParams() }
    },
  },
  mounted () {
    this.$nextTick(() => { this._initialMount = false })
    if (!(this.$route.query?.iframe && this.$route.query?.token)) {
      this.fetchData()
    }
  },
  beforeDestroy () {
    this.getCampaignsController?.abort()
    this.getTagsController?.abort()
    this.loadMoreController?.abort()
  },
  created () {
    this._initialMount = true
    if (this.$route.query?.iframe) {
      const params = this.$route.query

      if (params.token) {
        const token = params.token.replaceAll('%7C', '|')
        this.$store.commit('headerAuth/setToken', token)
        setCookie(token, 14, 'cgToken')
        this.$store.commit('app/setHideHeader', true)
        this.$store.commit('app/setHideUserMenu', true)
        this.$store.dispatch('tooltips/getTooltips', null)

        this.fetchData()
      }
    }
  },
  updated () {
    if (this.isPageInIframe) {
      const helpButton = document.querySelector('#launcher')
      if (helpButton) helpButton.style.display = 'none'
    }
  },
  beforeRouteLeave (to, from, next) {
    if (this.isPageInIframe) {
      this.$store.dispatch('headerAuth/logout')
    }

    next()
  },
  methods: {
    ...mapActions(['fetchPageData', 'loadMoreCampaigns']),
    fetchData () {
      this.loading = true
      this.getCampaignsController?.abort()
      this.getTagsController?.abort()
      this.loadMoreController?.abort()
      this.getCampaignsController = new AbortController()
      this.getTagsController = new AbortController()
      this.loadMoreController = null

      this.fetchPageData({
        params: this.queryParams,
        campaignsSignal: this.getCampaignsController.signal,
        tagsSignal: this.getTagsController.signal,
      })
        .then(({ permissions, totalSendsFailed, tags }) => {
          this.userPermissions = permissions
          this.failedAutomationActionsCount = totalSendsFailed
          this.campaignTags = tags
          this.loading = false

          this.$nextTick(() => { this.handleRouteParams() })
        })
        .catch((e) => {
          if (!axios.isCancel(e)) {
            this.loading = false
          }
        })
        .finally(() => {
          this.getCampaignsController = null
          this.getTagsController = null
        })
    },
    handleLoadMore () {
      this.loadMoreController?.abort()
      this.loadMoreController = new AbortController()
      this.loadMoreCampaigns({ params: this.queryParams, signal: this.loadMoreController.signal })
        .finally(() => { this.loadMoreController = null })
    },
    handleRouteParams () {
      const { routeParams } = this
      if (routeParams?.tags) {
        const tagValue = decodeURIComponent(routeParams.tags.replace(/\+/g, ' '))
        const normalizeString = (str) => str.toLowerCase().replace(/[_-]/g, ' ').trim()
        const normalizedTagValue = normalizeString(tagValue)
        const foundTag = this.campaignTags.find(tag => {
          return normalizeString(tag.name) === normalizedTagValue || String(tag.id) === String(tagValue)
        })
        if (foundTag && this.selectedTag !== foundTag.id) {
          this.selectedTag = foundTag.id
        }
      }
      if (routeParams?.panel === 'integration' && routeParams?.campaign_id) {
        const selectedCampaign = this.campaignItems.find(({ entity_id }) => entity_id === Number(routeParams.campaign_id))
        if (selectedCampaign) {
          this.showAutomationPanel(selectedCampaign)
        } else {
          this.clearRouteParams()
        }
      }
    },
    clearRouteParams () {
      if (Object.keys(this.routeParams).length) {
        this.$router.replace({ query: {} })
      }
    },
    showGiftOptionsPanel (product) {
      this.setSelectedGift(product)
      this.giftOptionsPanel = true
    },
    showEGiftPanel (product, type = 'egift') {
      const { display_in_view_mode: showViewMode } = product

      this.setSelectedGift(product)
      this.sendEGiftPanelType = type

      this.sendEGiftPanel = !!showViewMode
      this.sendEGiftPanelOld = !showViewMode
    },
    showEstCostPanel (product) {
      this.setSelectedGift(product)
      this.estCostPanel = true
    },
    showAutomationPanel (product) {
      this.setSelectedGift(product)
      this.automationPanel = true
    },
    showCampaignSettingsPanel (product) {
      this.setSelectedGift(product)
      this.campaignSettingsPanel = true
    },
    showSendDirectPanel (product) {
      const { collection: { items } } = product
      const {
        product_id: productId,
        link,
        product: {
          type: {
            is_configurable: isConfigurable
          },
        }
      } = items[0]
      if (isConfigurable) {
        window.open(link, '_blank')
      } else {
        Api.post('/cart/items', [{ qty: 1, product_id: Number(productId), redirect_url: link }])
          .then(() => {
            this.$cgToast.successBold('Successfully added product to cart')
            this.updateCartAmount()
            this.$store.commit('header/setShowCart', true, { root: true })
          })
          .catch((e) => {
            console.error(e)
            this.$cgToast.error('An error occurred, please contact our support')
          })
      }
    },
    showFailedAutomatedPhysicalSends () {
      this.failedAutomatedPhysicalSendsPanel = true
    },
    setSelectedGift (product) {
      this.selectedGift = product
    },
  }
}
</script>

<style lang="scss">
.my-campaigns-page {
  .v-select__slot {
    padding: 0 5px;
  }

  .v-text-field {
    > .v-input__control {
      > .v-input__slot:after {
        border: none;
      }
    }
  }

  .v-input__slot {
    input {
      &::placeholder {
        font-family: 'Lato-Italic';
        font-style: normal;
        font-size: 14px;
        line-height: 17px;
        color: #a1a1a1;
      }
    }

    &::before {
      border-color: rgba(0, 0, 0, 0) !important;
    }
  }

  .v-select__slot .v-input__icon i {
    margin-left: 0;

    &::before {
      color: #919191;
      font-size: 20px;
    }
  }

  &__filter {
    line-height: 16px;
    white-space: nowrap;
  }

  &__title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;

    span {
      color: #000;
      font-family: 'Lato-Light', sans-serif;
      line-height: 65px;
      font-size: 40px;
      letter-spacing: 0;
      font-weight: 300;
    }

    button,
    a {
      border-radius: 100px;
    }
  }

  &__empty {
    padding: 50px 0;
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: center;

    span {
      max-width: 512px;
      text-align: center;
      font-size: 20px;
      font-weight: 400;
      font-family: 'Lato-Italic', sans-serif;
      line-height: 29.9px;
      color: #222325;
    }

    button,
    a {
      border-radius: 100px;
    }
  }

  &__load-more {
    display: flex;
    justify-content: center;
    padding: 32px 0;
  }

  &__failed-physical-automation-sends {
    .cg-alert .cg-alert__content {
      display: flex;
      gap: 16px;
      font-weight: normal;

      > div {
        &:last-child {
          color: #222325;
          font-size: 16px;
          font-style: normal;
          font-weight: 700;
          line-height: 136%;
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  }
}

.campaigns-page-select {
  padding: 0 !important;
  z-index: 4 !important;

  .v-list {
    padding: 0;

    > .v-list-item {
      padding: 2px 10px !important;
      max-height: 32px !important;
      min-height: 32px !important;
      color: #16181F !important;

      &:hover {
        background-color: #F5F5F5;
        color: #16181F !important;
      }
    }
  }
}
</style>
